import React, { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Node, useReactFlow } from 'reactflow';
import { clearMultiSelection } from '@components/MainStage/clearMultiSelection';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import { canvasState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { EditNodeDrawerMap } from '@constants/entities/drawers';
import { useProject } from '@context/Project';
import useResetViewport from '@hooks/canvas/useResetViewport';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import { useUpdateNodePositionMutation } from '@store/services/nodes';
import {
  clampGroupDelta,
  getVisibleSelectedNodes,
} from '@utils/canvas/groupDrag';
import NodeView from '@utils/canvas/NodeView';
import { isElementLocal } from '@utils/drawers';

interface GroupDragState {
  ids: string[];
  startPositions: Map<string, { x: number; y: number }>;
  startPointer: { x: number; y: number };
}

const editNodeDrawerTypes = new Set<string>(Object.values(EditNodeDrawerMap));

const useDragNode = ({
  menu,
  closeMenu,
  closeNodeDrawerOnForeignDrag = false,
}: {
  menu: any;
  closeMenu: () => void;
  closeNodeDrawerOnForeignDrag?: boolean;
}) => {
  const nodeDragRef = useRef<Node | null>(null);
  const groupDragRef = useRef<GroupDragState | null>(null);
  const closedDrawerOnDragRef = useRef(false);
  const { drawer, hasChanges, handleCloseDrawer } = useProject();
  const resetViewPort = useResetViewport();
  const [updateNodePositionRequest] = useUpdateNodePositionMutation();
  const { selectedAssetId } = useAssets();
  const projectId = useProjectId(true);
  const reactFlow = useReactFlow();

  const {
    updateNodePosition,
    updateNodePositions,
    updateNode,
    unselectActiveEdge,
    unselectActiveNode,
  } = canvasState();

  const persistPositions = useCallback(
    (updatedNodes: { id: string; x: number; y: number }[]) => {
      const requestParams = {
        projectId,
        ...(selectedAssetId && { asset_id: selectedAssetId }),
        nodes: updatedNodes,
      };

      updateNodePositionRequest(requestParams).unwrap();
    },
    [projectId, selectedAssetId],
  );

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const activeEdge = canvasState().getActiveEdge();
      if (!activeEdge || !isElementLocal(activeEdge.id)) unselectActiveEdge();

      if (menu) closeMenu();
      nodeDragRef.current = node;

      const allNodes = canvasState().nodes;
      const { selectedIds } = getMultiSelectionState();
      const groupNodes = getVisibleSelectedNodes(selectedIds, allNodes);
      const isGroupDrag =
        groupNodes.length >= 2 &&
        groupNodes.some((groupNode) => groupNode.id === node.id);

      const wasSelected = Boolean(
        allNodes.find((candidate) => candidate.id === node.id)?.selected,
      );

      if (isGroupDrag || !wasSelected) unselectActiveNode();

      if (
        closeNodeDrawerOnForeignDrag &&
        !wasSelected &&
        !hasChanges &&
        drawer &&
        editNodeDrawerTypes.has(drawer.type)
      ) {
        flushSync(() => {
          handleCloseDrawer();
        });
        closedDrawerOnDragRef.current = true;
      }

      if (isGroupDrag) {
        const startPositions = new Map(
          groupNodes.map((groupNode) => [
            groupNode.id,
            { x: groupNode.position.x, y: groupNode.position.y },
          ]),
        );

        groupDragRef.current = {
          ids: groupNodes.map((groupNode) => groupNode.id),
          startPositions,
          startPointer: { x: event.clientX, y: event.clientY },
        };

        groupNodes.forEach((groupNode) =>
          updateNode(groupNode.id, { extent: undefined }),
        );
      } else {
        groupDragRef.current = null;
        clearMultiSelection();
      }
    },
    [menu, closeNodeDrawerOnForeignDrag, drawer, hasChanges, handleCloseDrawer],
  );

  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const groupDrag = groupDragRef.current;
      if (!groupDrag) return;

      if (!groupDrag.startPositions.has(node.id)) return;

      const allNodes = canvasState().nodes;
      const groupNodes = allNodes.filter((candidate) =>
        groupDrag.ids.includes(candidate.id),
      );

      const zoom = reactFlow.getZoom() || 1;
      const raw = {
        dx: (event.clientX - groupDrag.startPointer.x) / zoom,
        dy: (event.clientY - groupDrag.startPointer.y) / zoom,
      };

      const { dx, dy } = clampGroupDelta(
        groupNodes,
        groupDrag.startPositions,
        allNodes,
        raw,
      );

      updateNodePositions(
        groupDrag.ids.map((id) => {
          const memberStart = groupDrag.startPositions.get(id)!;

          return { id, x: memberStart.x + dx, y: memberStart.y + dy };
        }),
      );
    },
    [reactFlow],
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const groupDrag = groupDragRef.current;

      if (closedDrawerOnDragRef.current) {
        closedDrawerOnDragRef.current = false;
        resetViewPort();
      }

      if (node.type === NodesTypes.NewNode) {
        groupDragRef.current = null;
        nodeDragRef.current = null;
        return;
      }

      if (groupDrag) {
        const allNodes = canvasState().nodes;

        const updatedNodes = groupDrag.ids
          .map((id) => {
            const memberNode = allNodes.find(
              (candidate) => candidate.id === id,
            );
            if (!memberNode) return null;

            const snappedY = new NodeView(id, memberNode).YPosition;

            return { id, x: memberNode.position.x, y: snappedY };
          })
          .filter(
            (updated): updated is { id: string; x: number; y: number } =>
              updated !== null,
          );

        updateNodePositions(updatedNodes.map(({ id, y }) => ({ id, y })));

        groupDrag.ids.forEach((id) => updateNode(id, { extent: 'parent' }));

        persistPositions(updatedNodes);

        groupDragRef.current = null;
        nodeDragRef.current = null;
        return;
      }

      const nodeView = new NodeView(node.id);

      if (nodeDragRef.current) {
        updateNodePosition(nodeDragRef.current.id, { y: nodeView.YPosition });
        persistPositions([
          { id: node.id, x: node.position.x, y: nodeView.YPosition },
        ]);
      }

      nodeDragRef.current = null;
    },
    [persistPositions, resetViewPort],
  );

  return { onNodeDragStart, onNodeDrag, onNodeDragStop };
};

export default useDragNode;
